import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, User, LogOut, ShoppingBag } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ASSETS } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useCart } from "@/lib/CartContext";
import { useLocale } from "@/lib/LocaleContext";
import { localisedTo } from "@/lib/i18nRoutes";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import CurrencySelector, { CurrencyNotice } from "@/components/CurrencySelector";
import BalancePill from "@/components/BalancePill";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [menuUser, setMenuUser] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { items: cartItems, packs: cartPacks, open: openCart } = useCart();
  const { lang } = useLocale();
  const { t } = useTranslation();
  const cartCount = cartItems.length + cartPacks.length;

  // Localised nav links. Section anchors stay identical for both locales
  // (they resolve to the same DOM ids on the homepage).
  const links = [
    { href: "#home", label: t("nav.home"), key: "home" },
    { href: "#about", label: t("nav.about"), key: "about" },
    { to: "/music", label: t("nav.music"), key: "music" },
    { to: "/arabic-remixes", label: t("nav.arabicRemixes"), key: "arabicRemixes" },
    { to: "/blog", label: t("nav.blog"), key: "blog" },
    { to: "/media", label: t("nav.media"), key: "media" },
    { href: "#contact", label: t("nav.contact"), key: "contact" },
  ];

  const localised = (path) => localisedTo(path, lang);
  const homePath = localised("/");

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleAnchor = (e, href) => {
    e.preventDefault();
    setOpen(false);
    // Anchor links only make sense on the localised home page.
    if (location.pathname !== "/" && location.pathname !== "/ar" && location.pathname !== "/ar/") {
      navigate(`${homePath}${homePath.endsWith("/") ? "" : ""}${href}`);
      return;
    }
    const id = href.replace("#", "");
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${
        scrolled ? "backdrop-blur-2xl bg-black/70 border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
        <Link to={homePath} data-testid="nav-logo" className="flex items-center gap-3 shrink-0">
          <span className="relative inline-flex items-start">
            <img src={ASSETS.logo} alt="DJ SPY Official logo" className="h-10 md:h-12 w-auto max-w-none shrink-0" fetchPriority="high" />
            <sup
              aria-hidden="true"
              data-testid="nav-logo-tm"
              className="text-white/70 text-[9px] md:text-[10px] font-mono leading-none ml-0.5 mt-0.5 select-none"
            >
              ®
            </sup>
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-6 lg:gap-8 whitespace-nowrap">
          {links.map((l) => {
            const isMedia = l.key === "media";
            const activeMedia = isMedia && /^\/(ar\/)?media(\/|$)/.test(location.pathname);
            const track = () => {
              // Consent-gated header click event.
              try { window.__consent?.granted && window.dispatchEvent(new CustomEvent("analytics", { detail: { event: "header_media_click" } })); } catch (_) {}
            };
            return l.to ? (
              <Link
                key={l.key}
                to={localised(l.to)}
                data-testid={`nav-${l.key}`}
                onClick={() => { setOpen(false); if (isMedia) track(); }}
                aria-current={activeMedia ? "page" : undefined}
                className={`text-sm transition-colors relative group ${activeMedia ? "text-white" : "text-white/75 hover:text-white"}`}
              >
                {l.label}
                <span className={`absolute -bottom-1 left-0 h-px bg-red-brand transition-all duration-300 ${activeMedia ? "w-full" : "w-0 group-hover:w-full"}`} />
              </Link>
            ) : (
              <a
                key={l.key}
                href={l.href}
                data-testid={`nav-${l.key}`}
                onClick={(e) => handleAnchor(e, l.href)}
                className="text-sm text-white/75 hover:text-white transition-colors relative group"
              >
                {l.label}
                <span className="absolute -bottom-1 left-0 h-px w-0 bg-red-brand group-hover:w-full transition-all duration-300" />
              </a>
            );
          })}

          {user ? (
            <div className="relative">
              <button
                data-testid="nav-user"
                onClick={() => setMenuUser((v) => !v)}
                className="inline-flex items-center gap-2 border border-white/15 rounded-full px-4 py-2 text-sm hover:border-red-brand transition-colors"
              >
                <User size={14} /> {user.name || user.email.split("@")[0]}
              </button>
              {menuUser && (
                <div className="absolute right-0 mt-2 w-56 bg-black border border-white/10 rounded-xl overflow-hidden">
                  <Link
                    to={localised("/library")}
                    data-testid="nav-library"
                    className="block px-4 py-3 text-sm hover:bg-white/5"
                    onClick={() => setMenuUser(false)}
                  >
                    {t("nav.myLibrary")}
                  </Link>
                  {user.role === "admin" && (
                    <Link
                      to="/admin/dashboard"
                      data-testid="nav-admin"
                      className="block px-4 py-3 text-sm hover:bg-white/5"
                      onClick={() => setMenuUser(false)}
                    >
                      {t("nav.adminDashboard")}
                    </Link>
                  )}
                  <button
                    data-testid="nav-logout"
                    onClick={() => { logout(); setMenuUser(false); navigate(homePath); }}
                    className="block w-full text-left px-4 py-3 text-sm hover:bg-white/5 border-t border-white/5"
                  >
                    <LogOut size={12} className="inline mr-2" /> {t("nav.signOut")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link
              to={localised("/login")}
              data-testid="nav-signin"
              className="text-sm text-white/75 hover:text-white transition-colors"
            >
              {t("nav.signIn")}
            </Link>
          )}

          <LanguageSwitcher variant="header" />
          <CurrencySelector variant="header" />

          <BalancePill />

          <button
            type="button"
            onClick={openCart}
            data-testid="nav-cart"
            aria-label={t("nav.cartItems", { count: cartCount })}
            className="relative inline-flex items-center justify-center w-10 h-10 rounded-full border border-white/15 hover:border-red-brand transition-colors"
          >
            <ShoppingBag size={16} />
            {cartCount > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1.5 rounded-full bg-red-brand text-white text-[10px] font-mono flex items-center justify-center leading-none"
                dir="ltr"
              >
                {cartCount}
              </span>
            )}
          </button>

          <a
            href="#contact"
            onClick={(e) => handleAnchor(e, "#contact")}
            data-testid="nav-book"
            className="btn-primary !py-2.5 !px-5 text-sm"
          >
            {t("nav.bookNow")}
          </a>
        </nav>

        <div className="md:hidden flex items-center gap-2">
          <LanguageSwitcher variant="header-mobile" />
          <CurrencySelector variant="header-mobile" />
          <button
            type="button"
            onClick={openCart}
            data-testid="nav-mobile-cart"
            aria-label={t("nav.cartItems", { count: cartCount })}
            className="relative inline-flex items-center justify-center w-10 h-10 text-white"
          >
            <ShoppingBag size={22} />
            {cartCount > 0 && (
              <span
                data-testid="nav-mobile-cart-count"
                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-4 px-1 rounded-full bg-red-brand text-white text-[9px] font-mono flex items-center justify-center leading-none"
                dir="ltr"
              >
                {cartCount}
              </span>
            )}
          </button>
          <button
            data-testid="nav-menu-toggle"
            className="text-white"
            onClick={() => setOpen((v) => !v)}
            aria-label={t("nav.menu")}
          >
            {open ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden bg-black/95 border-t border-white/10">
          <div className="px-6 py-6 flex flex-col gap-4">
            {links.map((l) => {
              const isMedia = l.key === "media";
              const activeMedia = isMedia && /^\/(ar\/)?media(\/|$)/.test(location.pathname);
              const track = () => {
                try { window.__consent?.granted && window.dispatchEvent(new CustomEvent("analytics", { detail: { event: "mobile_menu_media_click" } })); } catch (_) {}
              };
              return l.to ? (
                <Link
                  key={l.key}
                  to={localised(l.to)}
                  data-testid={`nav-mobile-${l.key}`}
                  onClick={() => { setOpen(false); if (isMedia) track(); }}
                  aria-current={activeMedia ? "page" : undefined}
                  className={`text-lg ${activeMedia ? "text-white border-l-2 border-red-brand pl-3" : "text-white/85"}`}
                >
                  {l.label}
                </Link>
              ) : (
                <a
                  key={l.key}
                  href={l.href}
                  data-testid={`nav-mobile-${l.key}`}
                  onClick={(e) => handleAnchor(e, l.href)}
                  className="text-lg text-white/85"
                >
                  {l.label}
                </a>
              );
            })}
            {user ? (
              <>
                <Link to={localised("/library")} data-testid="nav-mobile-library" className="text-lg text-white/85" onClick={() => setOpen(false)}>
                  {t("nav.myLibrary")}
                </Link>
                {user.role === "admin" && (
                  <Link to="/admin/dashboard" data-testid="nav-mobile-admin" className="text-lg text-white/85" onClick={() => setOpen(false)}>
                    {t("nav.adminDashboard")}
                  </Link>
                )}
                <button data-testid="nav-mobile-logout" onClick={() => { logout(); setOpen(false); navigate(homePath); }} className="text-left text-lg text-white/70">
                  {t("nav.signOut")}
                </button>
              </>
            ) : (
              <>
                <Link to={localised("/login")} data-testid="nav-mobile-signin" className="text-lg text-white/85" onClick={() => setOpen(false)}>{t("nav.signIn")}</Link>
                <Link to={localised("/signup")} data-testid="nav-mobile-signup" className="text-lg text-white/85" onClick={() => setOpen(false)}>{t("nav.createAccount")}</Link>
              </>
            )}
            <a
              href="#contact"
              onClick={(e) => handleAnchor(e, "#contact")}
              data-testid="nav-mobile-book"
              className="btn-primary inline-block text-center mt-2"
            >
              {t("nav.bookNow")}
            </a>
          </div>
        </div>
      )}
      <CurrencyNotice />
    </header>
  );
}
